import { useCallback, useRef } from 'react';

import { useAuth } from '../firebase/FirebaseContext';
import { isInAppBrowser } from '../lib/inAppBrowser';
import { requestEmailOtp, verifyEmailOtp } from '../callables';
import { signInWithCustomTokenAndMerge } from './accountAuth';
import { useSetMergeStatus } from './MergeStateContext';

/**
 * OTP sign-in — the one login mechanism. A 6-digit email code that works
 * identically in the Instagram/Facebook webview and in real browsers, so the
 * OAuth-in-webview failure modes simply stop existing.
 *
 * Two callables (`theaWebRequestEmailOtp` → send code; `theaWebVerifyEmailOtp`
 * → verify + mint a Firebase custom token) plus `signInWithCustomTokenAndMerge`
 * (accountAuth) on the SINGLETON auth instance (via `useAuth()` — NEVER a bare
 * `getAuth()`, which would spin up a second instance bypassing the
 * force-long-polling / IndexedDB-in-read-path init settings; the
 * noBareFirebaseInstances fitness test enforces this). Mirrors `useResumeToken`,
 * which redeems its token the same way.
 *
 * MERGE-ON-COLLISION: an anon user A with a board who OTP-verifies an email
 * that already belongs to permanent account B must not lose A's board. The
 * sign-in reuses the EXACT OAuth merge machinery — `signInWithCustomTokenAndMerge`
 * mints a merge token while still anon (`captureMergeIntent`), drives
 * `MergeStateContext` ('merging' before the auth swap so `useRecommendationDoc`
 * holds prior state), signs in, then fires `mergeGiftFlow({fromUid: A, token})`
 * iff the uid actually changed (login-into-existing / fresh account). An
 * upgrade-in-place (same uid) skips the merge. No schema change, no new merge
 * path — the FE decides purely by comparing the uid before/after sign-in.
 *
 * WEBVIEW APP-SWITCH SURVIVAL — the load-bearing subtlety:
 * To read the code the user leaves the webview for their Mail app and comes
 * back. IG/FB webviews wipe localStorage/IndexedDB on that round-trip, so the
 * whole SPA reloads fresh and any in-memory "awaiting code" state is gone. We
 * carry the pending state (email + step) in the URL via `history.replaceState`
 * — the same trick `useResumeToken` uses for the anon session token — so the
 * reloaded app lands the user back on the code screen instead of a blank email
 * step. History entries store the full URL, so the browser's own Back stack
 * and session-restore carry it with no further work.
 *
 * Privacy: `otp_email` is the user's OWN email, in their OWN address bar, and
 * only for the seconds between requesting and entering a code. Both the WRITE
 * and the READ of the pending state are gated on `shouldCarryInUrl()`
 * (in-app browser, or the `otpforce` test override) — so a URL casually copied
 * from Safari never carries it AND, if one is shared/pasted into a normal
 * browser, it is never consumed (the sign-in modal won't auto-open prefilled
 * with someone else's email). The params are scrubbed from GA4
 * (`scrubResumeToken` strips `otp_email`/`otp_step`) so they never reach
 * analytics, and cleared whenever the modal is dismissed. The pinned
 * `strict-origin-when-cross-origin` referrer policy keeps them off cross-origin
 * click-outs.
 */

const EMAIL_PARAM = 'otp_email';
const STEP_PARAM = 'otp_step';
const FORCE_PARAM = 'otpforce';

function shouldCarryInUrl(): boolean {
  if (typeof window === 'undefined') return false;
  if (new URLSearchParams(window.location.search).get(FORCE_PARAM) === '1') return true;
  return isInAppBrowser();
}

function readPendingFromUrl(): { email: string } | null {
  if (typeof window === 'undefined') return null;
  // Gate the READ on the same check as the WRITE. The pending state is only
  // ever WRITTEN inside an in-app browser (app-switch survival), so a URL that
  // legitimately carries it is always an in-app-browser URL. Reading it in a
  // normal browser only happens when someone shared/copied a URL that still
  // has the params — consuming it there would auto-open the sign-in modal
  // prefilled with a stranger's email. Skip it unless we're the browser that
  // would have written it (in-app, or the `otpforce` test override).
  if (!shouldCarryInUrl()) return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get(STEP_PARAM) !== 'code') return null;
  const email = params.get(EMAIL_PARAM);
  if (!email) return null;
  return { email };
}

function writePendingToUrl(email: string): void {
  if (!shouldCarryInUrl()) return;
  const url = new URL(window.location.href);
  if (url.searchParams.get(EMAIL_PARAM) === email && url.searchParams.get(STEP_PARAM) === 'code') {
    return;
  }
  url.searchParams.set(EMAIL_PARAM, email);
  url.searchParams.set(STEP_PARAM, 'code');
  window.history.replaceState(window.history.state, '', url.toString());
}

/**
 * Strip the pending-OTP params from the URL. Exported so the modal's close
 * paths can clear them on dismiss (privacy) without duplicating the param
 * logic. Ungated on purpose — clearing must always work, whatever the browser.
 */
export function clearPendingFromUrl(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has(EMAIL_PARAM) && !url.searchParams.has(STEP_PARAM)) return;
  url.searchParams.delete(EMAIL_PARAM);
  url.searchParams.delete(STEP_PARAM);
  window.history.replaceState(window.history.state, '', url.toString());
}

/**
 * True when the URL carries a pending OTP code step (webview app-switch
 * survival). AuthGateProvider reads this at mount to re-open the sign-in modal
 * onto the code screen after an in-app-browser reload wiped in-memory state.
 * Cheap + dependency-free so the gate can call it without pulling the hook.
 */
export function hasPendingOtpInUrl(): boolean {
  return readPendingFromUrl() !== null;
}

export interface UseOtpSignInResult {
  /**
   * Pending OTP state recovered from the URL at mount (webview app-switch
   * survival). When non-null, the modal should open straight to the code step
   * pre-filled with this email. Latched once at first render so a later URL
   * rewrite can't retrigger it.
   */
  initialPending: { email: string } | null;
  /** Request a code for `email`. Writes the URL pending state on success. */
  requestCode: (email: string) => Promise<void>;
  /**
   * Verify `code` for `email`. On success signs in with the returned custom
   * token (singleton auth) and clears the URL pending state. Throws on any
   * failure so the caller can surface the friendly error.
   */
  verifyCode: (email: string, code: string) => Promise<void>;
  /** Clear the URL pending state (e.g. the user backs out to the email step). */
  clearPending: () => void;
}

export function useOtpSignIn(): UseOtpSignInResult {
  const auth = useAuth();
  // Drives MergeStateContext so the anon → permanent board merge that runs on
  // a login-into-existing-account collision holds the board's prior state
  // instead of flashing "couldn't find this recommendation". No-op when no
  // MergeStateProvider is mounted (stories / isolated tests).
  const setMergeStatus = useSetMergeStatus();

  // Latch the URL pending state at mount — the same reason useResumeToken
  // latches its rt param: our own writes rewrite the URL, and a live read
  // after that would re-restore state we just set.
  const initialPendingRef = useRef<{ email: string } | null | undefined>(undefined);
  if (initialPendingRef.current === undefined) {
    initialPendingRef.current = readPendingFromUrl();
  }

  const requestCode = useCallback(async (email: string): Promise<void> => {
    // Write the pending marker BEFORE the round-trip, not after. The whole
    // point of the URL-carried state is surviving a webview app-switch — and
    // the likeliest moment to switch away is exactly while this request is in
    // flight (the user going to look for the code). Writing after the await
    // left that window unprotected. The marker is harmless if the send fails:
    // the modal is already on the code step, with Resend right there.
    writePendingToUrl(email);
    await requestEmailOtp({ email });
  }, []);

  const verifyCode = useCallback(
    async (email: string, code: string): Promise<void> => {
      const { data } = await verifyEmailOtp({ email, code });
      if (!data?.customToken) {
        // Contract says a verified code always returns a token; a missing one
        // is a server-side anomaly, not a user error. Surface as a failure.
        throw new Error('otp/no-custom-token');
      }
      // Sign in with the custom token AND, if this turns out to be a login into
      // a DIFFERENT (pre-existing) account, merge the anon board into it —
      // reusing the same OAuth merge machinery (captureMergeIntent +
      // mergeGiftFlow) driven through MergeStateContext. An upgrade-in-place
      // (same uid) skips the merge. A failed merge does NOT throw — the user is
      // signed in either way; the board is orphaned, never lost.
      await signInWithCustomTokenAndMerge(auth, data.customToken, setMergeStatus);
      clearPendingFromUrl();
    },
    [auth, setMergeStatus],
  );

  const clearPending = useCallback(() => {
    clearPendingFromUrl();
  }, []);

  return {
    initialPending: initialPendingRef.current,
    requestCode,
    verifyCode,
    clearPending,
  };
}
